import { DatabaseReference, QueryConstraint, Database, goOffline, goOnline } from '@angular/fire/database';
// Import firebase data manipulators
import { set as fireSet, update as fireUpdate, remove as fireRemove, push as firePush, onDisconnect } from '@angular/fire/database';
// Import firebase query functions
import { orderByChild, orderByKey, equalTo, startAt, endAt, limitToFirst, limitToLast } from '@angular/fire/database';
// Import firebase data types
import { object as fireObject, ref as fireRef, list as fireList, query as fireQuery } from '@angular/fire/database';
import { serverTimestamp } from 'firebase/database';
import { QueryChange } from "rxfire/database/interfaces";

import { QueryFn } from "./database";
import { HttpClient } from '@angular/common/http';
import { firstValueFrom, Observable, ReplaySubject } from 'rxjs';
import { first, map } from 'rxjs/operators';

import { AbstractDatabase, DatabaseRef, ListRef, ObjectRef } from "./database";

interface ConstraintInterceptor { constraints: QueryConstraint[], handleQuery: (queryConstraint) => any }

/**
 * Firebase Database abstraction layer with offline caching capabilities
 * 
 * We use the Cache API polyfill with indexDB as backend (see idx-cache.ts) for storing JSON
 * IndexDB support, all brosers'ish: https://caniuse.com/#feat=indexeddb
 * Cache API: https://developer.mozilla.org/en-US/docs/Web/API/Cache
 */
export class FireDatabase implements AbstractDatabase {

    public database: FireDatabase = null;
    private initialized: boolean = false;
    private cache: Cache = null;
    private enableDbCache: boolean = false;
    private offlineEnabled: boolean = false;
    private disconnected: boolean = false;
    private initSubject: ReplaySubject<boolean> = new ReplaySubject(1);

    constructor(private af: Database, private http: HttpClient) {
        // Init super database variable
        this.database = this;
    }

    private getCacheUsage(): Promise<StorageEstimate> {
        if(navigator && navigator.storage && navigator.storage.estimate) {
            // Estimate supported support: https://developer.mozilla.org/en-US/docs/Web/API/StorageEstimate
            return navigator.storage.estimate();
        }
        else {
            // Hardcode estimated quotes where estimate API is not available (on iOS sarfari ~50 mb)
            // See https://stackoverflow.com/questions/38218859/whats-the-size-limit-of-cache-storage-for-service-worker/40032335 
            return Promise.resolve({ quota: 50 * 1024 * 1024, usage: 0 });
        }
    }

    private async printCacheUsage() {
        let estimate = await this.getCacheUsage();
        let usage = Math.round(estimate.usage / 1024 / 102.4) / 10;
        let quota = Math.round(estimate.quota / 1024 / 102.4) / 10;
        let bytesLeft = estimate.quota - estimate.usage;
        console.info(`Cache is using ${usage} Mb out of ${quota} Mb. Bytes left: ${bytesLeft}`);
    }

    /**
    * Init database and its offline cache
    */
    public async init(enableDbCache: boolean, offlineContentCache: string): Promise<boolean> {
        if(this.initialized) console.warn("Database already initialized?");
        this.initialized = true;
        // Make sure that database goes online again after many hours of sleep on iOS,
        // as session service will not get any connection notifications under this condition.
        // https://github.com/StoryHunt/app/issues/657
        this.database.goOnline();

        this.enableDbCache = enableDbCache;
        // Make warning if caches are missing and we tried to enable the cache
        if(this.enableDbCache && typeof caches == 'undefined') console.warn("Caches is undefined, not caching DB results");

        // Skip caching if disabled or not available
        if(!this.enableDbCache || typeof caches == 'undefined') {
            // OBS: We could use the idxCache poly fill in order to implement offline without caches
            // to get more storage (> 50 MB on iOS)
            this.initSubject.next(true);
            return true;
        }

        // Open offline cache
        caches.open(offlineContentCache).then(cache => {
            this.cache = cache as any as Cache;
            // Check if cache quotes are ok
            this.getCacheUsage().then(async (estimate) => {
                let bytesLeft = estimate.quota - estimate.usage;
                // Disable caching if cache is nearly full
                if(bytesLeft < 2048) {
                    this.printCacheUsage();
                    console.warn("Database cache is full, disabling request cache.");
                    this.enableDbCache = false;
                }

                // Initialize app connection state
                await this.initConnected();

                this.initSubject.next(bytesLeft >= 2048 ? true : false);
            });
        });

        return this.awaitInit();
    }

    private initConnected() {
        // Get uncached version info from server
        let req = this.http.get("/version.json?" + Math.random());
        // Return Motes version
        let obs = req.pipe(first(), map(json => {
            let connected = json['version'] != null;
            this.disconnected = !connected;
        }));

        return obs.toPromise().catch(err => {
            this.disconnected = true;
            console.log("Could not get motes version.json, app disconnected");
        });
    }

    public serverTime(): Object {
        return serverTimestamp();
    }

    /**
    * Await initialization of database (and the database cache)
    */
    public awaitInit(): Promise<any> {
        return this.initSubject.pipe(first()).toPromise();
    }

    /**
    * Enable or disable offline mode
    * In offline mode the database will only return read results from the offline cache
    * or empty results if no cache is available. Offline reads will never be queried on Firebase.
    * This will also enable/disable the Firebase offline persistance mode, which enables queuing up write operations.
    */
    public enableOfflineMode(enabled: boolean) {
        if(enabled == this.offlineEnabled) return;

        // TODO: Enforce offline write (event when we have internet)
        // For now we will just allow these writes and inform the user about this,
        // as it seems impossible to enforce offline of Firebase Realtime Database
        if(enabled) {
            //firebase.firestore().disableNetwork();
        }
        else {
            //firebase.firestore().enableNetwork();
        }
        this.offlineEnabled = enabled;
    }


    /**
    * Watch network connection through firebase info entry
    */
    public watchNetworkConnected() : Observable<boolean> {
        const connectionRef = fireObject(fireRef(this.af, ".info/connected"));
        const isConnected = connectionRef.pipe(map((snap) => {
            return (snap?.snapshot.val() == true);
        }));
        return isConnected;
    }

    /**
    * Detect if network is connected or not according to firebase info
    */
    public async isNetworkConnected(): Promise<boolean> {
        return firstValueFrom(this.watchNetworkConnected());
    }

    /**
    * Get database list reference - cached version of Angular Fire list()
    * @param ref Database list reference
    * @param queryFunction Database query (compatible with old firebase format)
    */
    public list<T>(ref: string, queryFunction?: QueryFn): ListRef<T> {
        const docRef = fireRef(this.af, ref);
        let queryConstraints: QueryConstraint[] = null;

        if(queryFunction) {
            // Create an query interceptor object to mimic Firebase Database instance
            let queryInterpreter = this.createQueryInterceptorFire();
            // Execute the queries and convert to new angular fire format (v7 >)
            queryFunction(queryInterpreter as any);
            // Get the list of constraints
            queryConstraints = queryInterpreter.constraints;
        }

        const query = queryConstraints ? fireQuery(docRef, ...queryConstraints) : docRef;
        let queryChanges: Observable<QueryChange[]> = fireList(query);

        return {
            snapshotChanges: () => queryChanges.pipe(map( changes => {
                return changes.map( item => { return { type: item.event, payload: item.snapshot, key: item.snapshot.key }});
            })),
            valueChanges: () => queryChanges.pipe(map( changes => {
                return changes.map( item => { return item.snapshot?.val() });
            })),
            push: (value) => firePush(docRef, value),
            set: (value: any) => fireSet(docRef, value),
            update: (value: any) => fireUpdate(docRef, value),
            remove: () => fireRemove(docRef)
        }
    }

    /**
    * Get a database object reference - cached version of Angular Fire object()
    * @param ref Database reference string
    */
    public object<T>(ref: string): ObjectRef<T> {
        // Create firebase db object
        const reference = fireRef(this.af, ref);
        let object = fireObject(reference);
        return this.queryChangeToCompatRef<T>(object, reference);
    }

    private queryChangeToCompatRef<T>(object: Observable<QueryChange>, reference: DatabaseReference) : any {
        return {
            snapshotChanges: () => {
                return object.pipe( map(change => {
                    return {
                        payload: change.snapshot,
                        key: change.snapshot.key,
                    }
                }));
            },
            valueChanges: () => {
                return object.pipe( map(change => {
                    return change.snapshot?.val() as T;
                }));
            },
            set: (value: any) => fireSet(reference, value),
            update: (value: any) => fireUpdate(reference, value),
            remove: () => fireRemove(reference)
        }
    }

    /**
    * Wrap underlaying database reference function - abstraction of Angular Fire ref() for update, remove, push etc.
    * @param ref Database reference string or null
    * @returns Returns a Reference representing the location in the Database corresponding to the provided path.
    * If no path is provided, the Reference will point to the root of the Database.
    */
    public ref<T>(ref?: string): DatabaseRef<T> {
        if(this.offlineEnabled && this.enableDbCache) console.warn("Writing to database in offline mode ", ref);
        const reference = fireRef(this.af, ref);
        return {
            remove: () => fireRemove(reference),
            update: (val: object) => fireUpdate(reference, val),
            push: () => firePush(reference),
            set: (val: object) => fireSet(reference, val),
            onDisconnect: () => onDisconnect(reference),
            once: () => firstValueFrom(fireObject(reference).pipe( map( object => object.snapshot)))
        }
    }

    /**
    * Return fake db reference to intercept Firebase query params
    */
    private createQueryInterceptorFire() :  ConstraintInterceptor {
        // Create a fake db ref to intercept query params
        let interceptor = {
            constraints: [],
            handleQuery: (constraint: QueryConstraint) => {
                interceptor.constraints.push(constraint);
                return interceptor;
            },
            orderByChild: (child) => interceptor.handleQuery(orderByChild(child)),
            orderByKey: () => interceptor.handleQuery(orderByKey()),
            equalTo: (equalStr) => interceptor.handleQuery(equalTo(equalStr)),
            startAt: (startStr) => interceptor.handleQuery(startAt(startStr)),
            endAt: (endStr) => interceptor.handleQuery(endAt(endStr)),
            limitToFirst: (limit) => interceptor.handleQuery(limitToFirst(limit)),
            limitToLast: (limit) => interceptor.handleQuery(limitToLast(limit))
        }
        return interceptor;
    }

    public goOffline() {
        goOffline(this.af);
    }

    public goOnline() {
        goOnline(this.af);
    }
}
